<template>
  <div class="okr-premises-block">
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="d-flex align-items-center">
        <span class="font-weight-bolder mr-1">Premissas</span>
        <b-badge v-if="isEdited" variant="light-warning" class="font-small-2">
          Editadas
        </b-badge>
      </div>
      <div class="d-flex align-items-center">
        <b-button
          v-if="!loading && hasHistory && !editMode"
          variant="flat-primary"
          size="sm"
          class="p-0 mr-1"
          @click="enterEditMode"
        >
          <feather-icon icon="EditIcon" size="14" />
          Editar
        </b-button>
        <b-button
          variant="flat-secondary"
          size="sm"
          class="p-0"
          @click="expanded = !expanded"
        >
          <feather-icon :icon="expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
        </b-button>
      </div>
    </div>

    <b-collapse v-model="expanded">
      <div v-if="loading" class="text-center py-2">
        <b-spinner variant="secondary" small />
      </div>

      <div v-else-if="!hasHistory" class="text-muted font-small-3 py-1">
        As premissas serão preenchidas após o primeiro trimestre completo.
      </div>

      <template v-else>
        <div class="premises-grid">
          <div v-for="field in fields" :key="field.key" class="premises-field">
            <div class="text-muted font-small-3">{{ field.label }}</div>
            <template v-if="editMode">
              <b-form-input
                v-model.number="editValues[field.key]"
                type="number"
                size="sm"
                :min="0"
                class="mt-25"
              />
            </template>
            <template v-else>
              <div class="font-weight-bolder">{{ formatValue(field, premises[field.key]) }}</div>
              <div
                v-if="isEdited && originalValues && originalValues[field.key] !== premises[field.key]"
                class="text-muted font-small-2 mt-25"
              >
                {{ formatValue(field, originalValues[field.key]) }} → {{ formatValue(field, premises[field.key]) }}
              </div>
            </template>
          </div>
        </div>

        <div v-if="editMode" class="d-flex justify-content-end mt-1 gap-1">
          <b-button variant="flat-secondary" size="sm" @click="cancelEdit">Cancelar</b-button>
          <b-button variant="primary" size="sm" :disabled="saving" @click="saveEdit">
            <b-spinner v-if="saving" small class="mr-25" />
            Salvar
          </b-button>
        </div>
      </template>
    </b-collapse>
  </div>
</template>

<script>
import {
  BButton, BCollapse, BBadge, BSpinner, BFormInput,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { mapActions } from 'vuex'
import * as types from '../store/types'

const FIELDS = [
  { key: 'ea_quantity',      label: 'EA',          type: 'integer' },
  { key: 'ef_quantity',      label: 'EF',          type: 'integer' },
  { key: 'ep_quantity',      label: 'EP',          type: 'integer' },
  { key: 'ea_ef_efficiency', label: 'EA/EF',       type: 'percent' },
  { key: 'ef_ep_efficiency', label: 'EF/EP',       type: 'percent' },
  { key: 'ep_avg_ticket',    label: 'Ticket EP',   type: 'currency' },
]

export default {
  name: 'OkrPremisesBlock',
  components: { BButton, BCollapse, BBadge, BSpinner, BFormInput, FeatherIcon },

  props: {
    consultantId: { type: Number, required: true },
    year:         { type: Number, required: true },
    quarter:      { type: Number, required: true },
  },

  data() {
    return {
      expanded:       true,
      loading:        false,
      saving:         false,
      editMode:       false,
      hasHistory:     false,
      isEdited:       false,
      premises:       null,
      originalValues: null,
      editValues:     {},
      fields:         FIELDS,
    }
  },

  watch: {
    consultantId: 'loadPremises',
    year:         'loadPremises',
    quarter:      'loadPremises',
  },

  mounted() {
    this.loadPremises()
  },

  methods: {
    ...mapActions({
      fetchPremises: types.GET_OKR_PREMISES,
      savePremises:  types.UPDATE_OKR_PREMISES,
    }),

    async loadPremises() {
      if (!this.year || !this.quarter || !this.consultantId) return
      this.loading = true
      this.editMode = false
      try {
        const resp = await this.fetchPremises({
          consultantId: this.consultantId,
          year:         this.year,
          quarter:      this.quarter,
        })
        this.hasHistory     = resp.data.has_history
        this.isEdited       = resp.data.is_edited
        this.premises       = resp.data.premises
        this.originalValues = resp.data.original_values || null
      } catch {
        this.hasHistory = false
      } finally {
        this.loading = false
      }
    },

    enterEditMode() {
      this.editValues = { ...this.premises }
      this.editMode = true
    },

    cancelEdit() {
      this.editMode = false
    },

    async saveEdit() {
      this.saving = true
      try {
        const resp = await this.savePremises({
          consultantId: this.consultantId,
          payload: {
            year:    this.year,
            quarter: this.quarter,
            ...this.editValues,
          },
        })
        this.hasHistory     = resp.data.has_history
        this.isEdited       = resp.data.is_edited
        this.premises       = resp.data.premises
        this.originalValues = resp.data.original_values || null
        this.editMode       = false
        this.$emit('premises-updated', resp.data.premises)
      } catch {
        // error handled by global interceptor
      } finally {
        this.saving = false
      }
    },

    formatValue(field, value) {
      if (value == null) return '—'
      if (field.type === 'percent')  return `${Number(value).toFixed(0)}%`
      if (field.type === 'currency') return this.$options.filters?.toCurrency?.(value) ?? `R$ ${value}`
      return value
    },
  },
}
</script>

<style scoped>
.premises-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 0.75rem;
}
.premises-field {
  min-width: 0;
}
.gap-1 {
  gap: 0.5rem;
}
</style>
